/**
 * HATCH serializer (§3.2 decision 5). The data bag stores pre-computed
 * pattern segments for rendering; the WRITER reconstructs the real DXF
 * pattern definition rows (78/53/43/44/45/46/79/49) from the pattern name +
 * scale + angle using the same .pat library the renderer uses
 * (useHatchPatternStore → resolveHatchPattern, which also covers the
 * built-ins). Solid fill writes flag 70=1. Gradient hatches (Faz 7.6)
 * write the 450-470 gradient block with the OCS from_name contract.
 */

import type { Entity } from '../../../../state/store';
import type { DxfWriteContext } from '../types';
import type { DxfStreamWriter } from '../DxfStreamWriter';
import { entityPrologue } from './common';
import { resolveHatchPattern } from '../../../../state/useHatchPatternStore';
import { gradientDxfName, type GradientData } from '../../../../rendering/gradientFill';

const deg = (rad: number): number => ((rad * 180) / Math.PI + 360) % 360;

export function writeHatch(w: DxfStreamWriter, ctx: DxfWriteContext, e: Entity, owner: string): void {
    const d = e.data as Record<string, any>;
    const boundary = Array.isArray(d.boundary) ? (d.boundary as number[][]) : [];
    if (boundary.length < 3) return;
    const gradient = d.gradient as GradientData | undefined;
    const patternName = gradient ? 'SOLID' : String(d.pattern ?? 'ANSI31').toUpperCase();
    const solid = gradient ? true : patternName === 'SOLID' || !!d.fill;
    const scale = typeof d.scale === 'number' && d.scale > 0 ? d.scale : 1;
    const angleDeg = deg(typeof d.angle === 'number' ? d.angle : 0);
    const patDef = solid ? null : resolveHatchPattern(patternName);

    entityPrologue(w, ctx, e, 'HATCH', owner);
    w.pair(100, 'AcDbHatch');
    w.pair(10, 0).pair(20, 0).pair(30, 0); // elevation
    w.pair(210, 0).pair(220, 0).pair(230, 1); // extrusion
    w.pair(2, patternName);
    w.pair(70, solid ? 1 : 0); // solid fill flag
    w.pair(71, 0); // associative (boundary handles are not tracked in Faz 1)
    // One external polyline boundary path.
    w.pair(91, 1);
    w.pair(92, 1 | 2); // external + polyline
    w.pair(72, 0); // path has bulge
    w.pair(73, 1); // path is closed
    w.pair(93, boundary.length);
    boundary.forEach((p) => w.pair(10, p[0] ?? 0).pair(20, p[1] ?? 0));
    w.pair(97, 0); // source boundary object count
    // Hatch style / pattern type / pattern definition rows.
    w.pair(75, 0); // hatch style: nested (single loop → outer)
    w.pair(76, patDef ? 1 : 0); // pattern type: predefined when resolvable
    w.pair(52, angleDeg);
    w.pair(41, scale);
    w.pair(77, 0); // double
    const lines = patDef?.lines ?? (patDef ? [] : [{ angleDeg: 45, originX: 0, originY: 0, deltaX: 0, deltaY: -scale, dashes: [] }]);
    w.pair(78, lines.length);
    lines.forEach((ln) => {
        w.pair(53, ln.angleDeg);
        w.pair(43, ln.originX).pair(44, ln.originY);
        w.pair(45, ln.deltaX).pair(46, ln.deltaY);
        w.pair(79, ln.dashes.length);
        ln.dashes.forEach((dash) => w.pair(49, dash));
    });
    w.pair(98, 0); // seed point count
    // Gradient definition block (Faz 7.6, DXF 450-470).
    if (gradient) {
        const nColors = gradient.singleColor ? 1 : 2;
        w.pair(450, 1); // gradient (non-solid) fill
        w.pair(451, gradient.singleColor ? 1 : 0); // one-color flag
        w.pair(452, Math.max(0, Math.min(1, gradient.tint))); // tint value
        w.pair(453, nColors); // color count
        w.pair(460, gradient.angle); // angle in radians
        w.pair(461, gradientDxfName(gradient)); // OCS from_name contract
        w.pair(462, 1); // color tint (one-color ramp end)
        w.pair(463, gradient.color1); // gradient color 1
        if (nColors === 2) {
            w.pair(463, gradient.color2); // gradient color 2
        }
        w.pair(470, 'LINEAR'); // rendering program hint
    }
}
